import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useDataLayerValue } from '../../contextAPI/DataLayer';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import {
  getDocumentRepositoryByEmployeeId,
  createDocumentRepository,
  getDocumentRepositoryFolderById,
  getDocumentRepositoryFolders,
  getDocumentRepositorySubfolders,
  getDocumentRepositoryFiles,
  getDocumentRepositoryFolderFiles,
  deleteDocumentRepositoryFolder,
  deleteDocumentRepositoryFile
} from '../../constants/requests';
import Toolbar from './Toolbar';
import FolderList from './FolderList';
import FileList from './FileList';
import CreateFolderDialog from './CreateFolderDialog';
import UploadFileDialog from './UploadFileDialog';
import ConfirmDialog from './ConfirmDialog';
import RenameFolderDialog from './RenameFolderDialog';
import FileAccessDialog from './FileAccessDialog';
import FolderAccessDialog from './FolderAccessDialog';
import FileDetailsDialog from './FileDetailsDialog';

/**
 * Document Repository page.
 * Phase 2: Resolves "my repository" on load, derives current folder from URL,
 * validates folder when in a folder route.
 */
export default function DocumentRepository() {
  const [user] = useDataLayerValue();
  const employeeId = user?._id ?? '';
  const { request } = useLoading();
  const { showNotifications } = useNotifications();
  const { folderId: folderIdParam } = useParams();
  const navigate = useNavigate();

  const [repositoryId, setRepositoryId] = useState(null);
  const [repoLoading, setRepoLoading] = useState(true);
  const [repoError, setRepoError] = useState(null);
  const [folderValidating, setFolderValidating] = useState(false);
  const [breadcrumbPath, setBreadcrumbPath] = useState([
    { id: null, name: 'Document Repository' }
  ]);
  const [breadcrumbRefreshKey, setBreadcrumbRefreshKey] = useState(0);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadFileOpen, setUploadFileOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDetailsOpen, setFileDetailsOpen] = useState(false);
  const [fileAccessOpen, setFileAccessOpen] = useState(false);
  const [deleteFileOpen, setDeleteFileOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [folderAccessOpen, setFolderAccessOpen] = useState(false);

  const currentFolderId =
    folderIdParam && folderIdParam.trim() !== '' ? folderIdParam : null;

  const refreshFolders = useCallback(async () => {
    if (!repositoryId || !employeeId) return;
    setFoldersLoading(true);
    try {
      const response = currentFolderId
        ? await getDocumentRepositorySubfolders(currentFolderId, employeeId)
        : await getDocumentRepositoryFolders(
            repositoryId,
            { rootOnly: true },
            employeeId
          );
      if (response?.data?.success && Array.isArray(response?.data?.data)) {
        setFolders(response.data.data);
      } else {
        setFolders([]);
      }
    } catch (err) {
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [repositoryId, currentFolderId, employeeId, breadcrumbRefreshKey]);

  const refreshFiles = useCallback(async () => {
    if (!repositoryId || !employeeId) return;
    setFilesLoading(true);
    try {
      const response = currentFolderId
        ? await getDocumentRepositoryFolderFiles(
            currentFolderId,
            { employeeId },
            employeeId
          )
        : await getDocumentRepositoryFiles(
            repositoryId,
            { rootOnly: true, employeeId },
            employeeId
          );
      if (response?.data?.success && Array.isArray(response?.data?.data)) {
        setFiles(response.data.data);
      } else {
        setFiles([]);
      }
    } catch (err) {
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [repositoryId, currentFolderId, employeeId]);

  const openRenameFolder = (folder) => {
    setSelectedFolder(folder);
    setRenameFolderOpen(true);
  };

  const openDeleteFolder = (folder) => {
    setSelectedFolder(folder);
    setDeleteFolderOpen(true);
  };

  const openFolderAccess = (folder) => {
    setSelectedFolder(folder);
    setFolderAccessOpen(true);
  };

  const confirmDeleteFolder = async () => {
    const folderId = selectedFolder?._id ?? selectedFolder?.id;
    if (!folderId) return;
    setDeletingFolder(true);
    try {
      const response = await request(() =>
        deleteDocumentRepositoryFolder(folderId, employeeId)
      );
      if (response?.data?.success) {
        showNotifications('success', 'Folder deleted', 3000);
        setDeleteFolderOpen(false);
        setSelectedFolder(null);
        await refreshFolders();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to delete folder',
          5000
        );
      }
    } finally {
      setDeletingFolder(false);
    }
  };

  const openFileDetails = (file) => {
    setSelectedFile(file);
    setFileDetailsOpen(true);
  };

  const openFileAccess = (file) => {
    setSelectedFile(file);
    setFileAccessOpen(true);
  };

  const openDeleteFile = (file) => {
    setSelectedFile(file);
    setDeleteFileOpen(true);
  };

  const confirmDeleteFile = async () => {
    const fileId = selectedFile?._id ?? selectedFile?.id;
    if (!fileId) return;
    setDeletingFile(true);
    try {
      const response = await request(() =>
        deleteDocumentRepositoryFile(fileId, employeeId)
      );
      if (response?.data?.success) {
        showNotifications('success', 'File deleted', 3000);
        setDeleteFileOpen(false);
        setSelectedFile(null);
        await refreshFiles();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to delete file',
          5000
        );
      }
    } finally {
      setDeletingFile(false);
    }
  };

  // 2.2 Resolve "my repository" on load
  useEffect(() => {
    if (!employeeId) {
      setRepoLoading(false);
      setRepoError('You must be logged in to view the document repository.');
      return;
    }

    const resolveRepository = async () => {
      setRepoLoading(true);
      setRepoError(null);
      try {
        const response = await getDocumentRepositoryByEmployeeId(employeeId);

        if (response?.data?.success && response?.data?.data) {
          const repo = response.data.data;
          setRepositoryId(repo._id ?? repo.id);
          setRepoError(null);
          setRepoLoading(false);
          return;
        }

        setRepoError(
          response?.data?.message || 'Failed to load document repository.'
        );
        setRepositoryId(null);
      } catch (err) {
        if (err?.response?.status === 404) {
          try {
            const createResponse = await request(() =>
              createDocumentRepository(employeeId)
            );
            if (createResponse?.data?.success && createResponse?.data?.data) {
              const repo = createResponse.data.data;
              setRepositoryId(repo._id ?? repo.id);
              setRepoError(null);
              setRepoLoading(false);
              return;
            }
          } catch (createErr) {
            setRepoError(
              createErr?.response?.data?.message ||
                createErr?.message ||
                'Failed to create document repository.'
            );
            setRepositoryId(null);
          }
        } else {
          setRepoError(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load document repository.'
          );
          setRepositoryId(null);
        }
      } finally {
        setRepoLoading(false);
      }
    };

    resolveRepository();
  }, [employeeId]);

  // 2.3 Derive current folder from URL; validate folder when in folder route
  useEffect(() => {
    if (!repositoryId || !currentFolderId) {
      return;
    }

    setFolderValidating(true);

    const validateFolder = async () => {
      try {
        const response = await getDocumentRepositoryFolderById(
          currentFolderId,
          employeeId
        );
        if (!response?.data?.success || !response?.data?.data) {
          showNotifications(
            'warning',
            'Folder not found. Redirecting to repository root.',
            3000
          );
          navigate('/document-repository', { replace: true });
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          showNotifications(
            'warning',
            'Folder not found. Redirecting to repository root.',
            3000
          );
          navigate('/document-repository', { replace: true });
        }
      } finally {
        setFolderValidating(false);
      }
    };

    validateFolder();
  }, [repositoryId, currentFolderId, employeeId, navigate, showNotifications]);

  // 3.1 Build breadcrumb data from current location
  useEffect(() => {
    if (!repositoryId) return;

    if (!currentFolderId) {
      setBreadcrumbPath([{ id: null, name: 'Document Repository' }]);
      return;
    }

    const buildBreadcrumb = async () => {
      try {
        const path = [];
        let folderIdToFetch = currentFolderId;

        while (folderIdToFetch) {
          const response = await getDocumentRepositoryFolderById(
            folderIdToFetch,
            employeeId
          );
          if (!response?.data?.success || !response?.data?.data) break;

          const folder = response.data.data;
          const id = folder._id ?? folder.id;
          const name = folder.name ?? 'Folder';
          path.push({ id: String(id), name });

          const parentId = folder.folderId;
          folderIdToFetch =
            parentId != null && parentId !== '' ? String(parentId) : null;
        }

        path.reverse();
        setBreadcrumbPath([{ id: null, name: 'Document Repository' }, ...path]);
      } catch (err) {
        setBreadcrumbPath([
          { id: null, name: 'Document Repository' },
          { id: currentFolderId, name: '…' }
        ]);
      }
    };

    buildBreadcrumb();
  }, [repositoryId, currentFolderId, employeeId]);

  // 4.1 Fetch folders for current location (root or subfolders)
  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  // 5.1 Fetch files for current location (root or folder); employeeId filters by access
  useEffect(() => {
    refreshFiles();
  }, [refreshFiles]);

  if (repoLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          p: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading document repository…
        </Typography>
      </Box>
    );
  }

  if (repoError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" color="error">
          Document Repository
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {repoError}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!repositoryId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Document Repository</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Could not load your repository. Please try again.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (folderValidating && currentFolderId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          p: 2
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Checking folder…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Toolbar
        breadcrumbPath={breadcrumbPath}
        onNewFolder={() => setCreateFolderOpen(true)}
        onUploadFile={() => setUploadFileOpen(true)}
      />

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Folders
      </Typography>
      <FolderList
        folders={folders}
        loading={foldersLoading}
        onFolderClick={(id) => navigate(`/document-repository/folder/${id}`)}
        onRenameFolder={openRenameFolder}
        onDeleteFolder={openDeleteFolder}
        onManageAccess={openFolderAccess}
      />

      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mt: 2, mb: 1 }}
      >
        Files
      </Typography>
      <FileList
        files={files}
        loading={filesLoading}
        onViewFile={openFileDetails}
        onManageAccess={openFileAccess}
        onDeleteFile={openDeleteFile}
      />

      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        repositoryId={repositoryId}
        currentFolderId={currentFolderId}
        creatorId={employeeId}
        employeeId={employeeId}
        onSuccess={refreshFolders}
      />

      <UploadFileDialog
        open={uploadFileOpen}
        onClose={() => setUploadFileOpen(false)}
        repositoryId={repositoryId}
        currentFolderId={currentFolderId}
        creatorId={employeeId}
        employeeId={employeeId}
        onSuccess={refreshFiles}
      />

      <RenameFolderDialog
        open={renameFolderOpen}
        folder={selectedFolder}
        employeeId={employeeId}
        onClose={() => {
          setRenameFolderOpen(false);
          setSelectedFolder(null);
        }}
        onSaved={() => {
          refreshFolders();
          setBreadcrumbRefreshKey((k) => k + 1);
        }}
      />

      <ConfirmDialog
        open={deleteFolderOpen}
        title="Delete folder"
        description={`Delete "${
          selectedFolder?.name ?? 'this folder'
        }"? This cannot be undone.`}
        confirmText="Delete"
        loading={deletingFolder}
        onClose={() => {
          if (!deletingFolder) {
            setDeleteFolderOpen(false);
            setSelectedFolder(null);
          }
        }}
        onConfirm={confirmDeleteFolder}
      />

      <FileDetailsDialog
        open={fileDetailsOpen}
        fileId={selectedFile?._id ?? selectedFile?.id}
        employeeId={employeeId}
        onClose={() => {
          setFileDetailsOpen(false);
          setSelectedFile(null);
        }}
      />

      <FileAccessDialog
        open={fileAccessOpen}
        file={selectedFile}
        employeeId={employeeId}
        onClose={() => {
          setFileAccessOpen(false);
          setSelectedFile(null);
        }}
        onSaved={refreshFiles}
      />

      <FolderAccessDialog
        open={folderAccessOpen}
        folder={selectedFolder}
        employeeId={employeeId}
        onClose={() => {
          setFolderAccessOpen(false);
          setSelectedFolder(null);
        }}
        onSaved={refreshFolders}
      />

      <ConfirmDialog
        open={deleteFileOpen}
        title="Delete file"
        description="Delete this file from the repository? (The underlying Document Manager document is not deleted.)"
        confirmText="Delete"
        loading={deletingFile}
        onClose={() => {
          if (!deletingFile) {
            setDeleteFileOpen(false);
            setSelectedFile(null);
          }
        }}
        onConfirm={confirmDeleteFile}
      />
    </Box>
  );
}
